/**
 * LocalStorage Utility
 * Provides safe and consistent localStorage operations for the frontend-only application
 */

// Storage keys - centralized for easy management
export const STORAGE_KEYS = {
    HAS_UNVEILED: 'hasUnveiled',
    SELECTED_LANGUAGE: 'selectedLanguage',
    USER_PREFERENCES: 'userPreferences'
};

/**
 * Safely get item from localStorage
 * @param {string} key - The key to retrieve
 * @param {*} defaultValue - Default value if key doesn't exist or parsing fails
 * @returns {*} The parsed value or default value
 */
const getStorageItem = (key, defaultValue = null) => {
    try {
        if (typeof window === 'undefined') {
            return defaultValue;
        }

        const item = window.localStorage.getItem(key);

        if (item === null) {
            return defaultValue;
        }

        // Try to parse as JSON, fallback to string
        try {
            return JSON.parse(item);
        } catch {
            return item;
        }
    } catch (error) {
        console.warn(`Error reading localStorage key "${key}":`, error);
        return defaultValue;
    }
};

/**
 * Safely set item in localStorage
 * @param {string} key - The key to set
 * @param {*} value - The value to store
 * @returns {boolean} Success status
 */
const setStorageItem = (key, value) => {
    try {
        if (typeof window === 'undefined') {
            return false;
        }

        const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
        window.localStorage.setItem(key, serializedValue);
        return true;
    } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
        return false;
    }
};

/**
 * Safely remove item from localStorage
 * @param {string} key - The key to remove
 * @returns {boolean} Success status
 */
const removeStorageItem = (key) => {
    try {
        if (typeof window === 'undefined') {
            return false;
        }

        window.localStorage.removeItem(key);
        return true;
    } catch (error) {
        console.warn(`Error removing localStorage key "${key}":`, error);
        return false;
    }
};

/**
 * Clear all localStorage (use with caution)
 * @returns {boolean} Success status
 */
export const clearAllStorage = () => {
    try {
        if (typeof window === 'undefined') {
            return false;
        }

        window.localStorage.clear();
        return true;
    } catch (error) {
        console.warn('Error clearing localStorage:', error);
        return false;
    }
};

/**
 * Get all localStorage keys
 * @returns {string[]} Array of all keys
 */
export const getAllStorageKeys = () => {
    try {
        if (typeof window === 'undefined') {
            return [];
        }

        return Object.keys(window.localStorage);
    } catch (error) {
        console.warn('Error getting localStorage keys:', error);
        return [];
    }
};

// ========== SPECIFIC UTILITY FUNCTIONS ==========

/**
 * Language Preference Management
 */
export const languageStorage = {
    get: () => {
        return getStorageItem(STORAGE_KEYS.SELECTED_LANGUAGE, 'en');
    },

    set: (language) => {
        return setStorageItem(STORAGE_KEYS.SELECTED_LANGUAGE, language);
    },

    clear: () => {
        return removeStorageItem(STORAGE_KEYS.SELECTED_LANGUAGE);
    }
};

/**
 * Unveil Status Management (for intro animation)
 */
export const unveilStorage = {
    get: () => {
        return getStorageItem(STORAGE_KEYS.HAS_UNVEILED, false);
    },

    set: (hasUnveiled) => {
        return setStorageItem(STORAGE_KEYS.HAS_UNVEILED, hasUnveiled);
    },

    clear: () => {
        return removeStorageItem(STORAGE_KEYS.HAS_UNVEILED);
    }
};

/**
 * User Preferences Management
 */
export const preferencesStorage = {
    get: () => {
        return getStorageItem(STORAGE_KEYS.USER_PREFERENCES, {});
    },

    set: (preferences) => {
        return setStorageItem(STORAGE_KEYS.USER_PREFERENCES, preferences);
    },

    update: (newPreferences) => {
        const current = preferencesStorage.get();
        const updated = { ...current, ...newPreferences };
        return setStorageItem(STORAGE_KEYS.USER_PREFERENCES, updated);
    },

    clear: () => {
        return removeStorageItem(STORAGE_KEYS.USER_PREFERENCES);
    }
};

export default {
    getStorageItem,
    setStorageItem,
    removeStorageItem,
    clearAllStorage,
    getAllStorageKeys,
    languageStorage,
    unveilStorage,
    preferencesStorage,
    STORAGE_KEYS
}; 